"use client";

import { Badge, Box, HStack, Text, VStack } from "@chakra-ui/react";
import { useState } from "react";
import {
  MEILI_SEARCH_CONFIG,
  type MeiliDocumentSearchHit,
} from "@/features/search";
import SearchLoadMoreButton from "@/features/search/components/SearchLoadMoreButton";
import {
  PROFILE_SHAPING_EMPTY_STATES,
  PROFILE_SHAPING_LOADING_STATES,
  ProfileReviewSelectionActions,
} from "@/features/profile-shaping";
import GenericEmptyComponent from "@/shared/ui/GenericEmptyComponent";
import LoadingState from "@/shared/ui/LoadingState";
import SuggestedThoughtItem from "../../components/SuggestedThoughtItem";

export type SuggestedThoughtsReviewProps = {
  canSelectAllSuggestedThoughts: boolean;
  emptyState?: {
    icon?: "info" | "origin_source";
    message: string;
    title: string;
  };
  isBusy?: boolean;
  isExtracting?: boolean;
  isLoadingSuggestions: boolean;
  maxSuggestions: number;
  canLoadMoreSuggestions?: boolean;
  onConfirmSelectedSuggestedThoughts: () => void | Promise<void>;
  onCopySelectedSuggestedThoughts?: () => void | Promise<void>;
  onDeleteSelectedSuggestedThoughts: () => void | Promise<void>;
  onLoadMoreSuggestions?: () => void;
  onSelectAllSuggestedThoughts: () => void;
  onSuggestedThoughtReviewSync: () => void;
  onUnselectSelectedSuggestedThoughts: () => void;
  selectedSuggestedThoughtCount: number;
  searchQuery?: string;
  scopeItems?: {
    colorPalette?: "blue" | "gray" | "green" | "orange" | "purple" | "teal";
    label: string;
  }[];
  suggestions: MeiliDocumentSearchHit[];
};

export default function SuggestedThoughtsReviewList({
  canSelectAllSuggestedThoughts,
  emptyState,
  isBusy = false,
  isExtracting = false,
  isLoadingSuggestions,
  canLoadMoreSuggestions = false,
  onConfirmSelectedSuggestedThoughts,
  onCopySelectedSuggestedThoughts,
  onDeleteSelectedSuggestedThoughts,
  onLoadMoreSuggestions,
  onSelectAllSuggestedThoughts,
  onSuggestedThoughtReviewSync,
  onUnselectSelectedSuggestedThoughts,
  selectedSuggestedThoughtCount,
  searchQuery,
  scopeItems = [],
  suggestions,
}: SuggestedThoughtsReviewProps) {
  const [openSuggestedThoughtActionId, setOpenSuggestedThoughtActionId] =
    useState<string | null>(null);

  if (isExtracting) {
    return (
      <LoadingState
        loadingMessage={
          PROFILE_SHAPING_LOADING_STATES.extraction.loadingMessage
        }
        message={PROFILE_SHAPING_LOADING_STATES.extraction.message}
      />
    );
  }

  if (isLoadingSuggestions) {
    return (
      <LoadingState
        loadingMessage={
          PROFILE_SHAPING_LOADING_STATES.suggestions.loadingMessage
        }
        message={PROFILE_SHAPING_LOADING_STATES.suggestions.message}
      />
    );
  }

  if (suggestions.length === 0) {
    return (
      <GenericEmptyComponent
        icon={emptyState?.icon ?? "info"}
        title={
          emptyState?.title ?? PROFILE_SHAPING_EMPTY_STATES.suggestions.title
        }
        message={
          emptyState?.message ??
          PROFILE_SHAPING_EMPTY_STATES.suggestions.message
        }
      />
    );
  }

  return (
    <VStack align="stretch" spaceY="2">
      <HStack justifyContent="space-between" align="start" gap="3">
        <HStack gap="2" align="center" flexWrap="wrap">
          <Text fontWeight="bold">Suggestions</Text>
          <Badge variant="surface" colorPalette="gray">
            Showing {suggestions.length}
          </Badge>
          {canLoadMoreSuggestions ? (
            <Badge variant="surface" colorPalette="blue">
              More available
            </Badge>
          ) : null}
        </HStack>
        {scopeItems.length > 0 ? (
          <HStack gap="1.5" justify="flex-end" flexWrap="wrap">
            {scopeItems.map((item) => (
              <Badge
                key={item.label}
                variant="surface"
                colorPalette={item.colorPalette ?? "gray"}
              >
                {item.label}
              </Badge>
            ))}
          </HStack>
        ) : null}
      </HStack>

      <ProfileReviewSelectionActions
        canSelectAll={canSelectAllSuggestedThoughts}
        isBusy={isBusy || isExtracting}
        itemLabelPlural="suggested thoughts"
        itemLabelSingular="suggested thought"
        onClearSelection={onUnselectSelectedSuggestedThoughts}
        onSelectAll={onSelectAllSuggestedThoughts}
        primaryAction={{
          ariaLabel: "Confirm selected suggested thoughts",
          icon: "confirm",
          label: "Confirm selected",
          onClick: onConfirmSelectedSuggestedThoughts,
          tone: "primary",
        }}
        secondaryActions={[
          ...(onCopySelectedSuggestedThoughts
            ? [
                {
                  ariaLabel: "Copy selected suggested thoughts",
                  icon: "copy_clipboard" as const,
                  label: "Copy",
                  onClick: onCopySelectedSuggestedThoughts,
                },
              ]
            : []),
          {
            ariaLabel: "Delete selected suggested thoughts",
            icon: "delete_permanent",
            label: "Delete",
            onClick: onDeleteSelectedSuggestedThoughts,
            tone: "danger",
          },
        ]}
        selectedCount={selectedSuggestedThoughtCount}
      />

      <Box>
        <VStack align="stretch">
          {suggestions.map((thought) => {
            const thoughtId = thought.objectID as string;
            const isMobileActionsOpen =
              openSuggestedThoughtActionId === thoughtId;

            return (
              <Box key={thoughtId}>
                <SuggestedThoughtItem
                  key={`${thoughtId}:${isMobileActionsOpen ? "open" : "closed"}`}
                  config={MEILI_SEARCH_CONFIG.suggested_thoughts}
                  hit={thought}
                  isInSearchContext={false}
                  isMobileActionsOpen={isMobileActionsOpen}
                  onMobileActionsOpenChange={(isOpen) => {
                    setOpenSuggestedThoughtActionId(isOpen ? thoughtId : null);
                  }}
                  onDeleteThoughtSuccess={onSuggestedThoughtReviewSync}
                  onThoughtUpdateSuccess={onSuggestedThoughtReviewSync}
                  searchQuery={searchQuery}
                />
              </Box>
            );
          })}
        </VStack>
        {canLoadMoreSuggestions && onLoadMoreSuggestions ? (
          <SearchLoadMoreButton onClick={onLoadMoreSuggestions} />
        ) : null}
      </Box>
    </VStack>
  );
}
